import React, { useCallback, useState } from 'react';
import { GoalId, GoalsFilterType, isGoalFilterType } from './types';
import { GoalSidebar } from './GoalSidebar/GoalSidebar';
import { useGoalFilter } from './useGoalFilter';
import { useSelectedGoal } from './useSelectedGoal';
import { GoalFilter } from './GoalsFilter/GoalsFilter';
import { GoalsListWithFilter } from './GoalsList/GoalsListWithFilter';
import { GoalPeriodSelector } from './GoalPeriodSelector/GoalPeriodSelector';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { t } from 'i18next';
import { Link } from 'react-router-dom';
import { useGoalsContext } from './GoalsContext/GoalsContext';
import { CREATE_GOAL_ID } from '../goalsLinks';
import { ObjectivePhaseActionType } from '../../../__generated__/graphql';
import Button from '../../../../../components/button/index';
import { PullGoalModal } from './PullGoalModal/PullGoalModal';
import { managerRoutes } from '../../../manager/routes';
import { GoalSelectionProvider, BulkApprovalBar } from './GoalBulkApproval';

const i18prefix = 'performanceReview.goals2';

type Props = {};

export const GoalsPage: React.FC<React.PropsWithChildren<Props>> = () => {
  const {
    developmentFrameworkEnabled,
    isManagerView,
    currentPeriod,
    hasAchievementScales,
  } = useGoalsContext();

  // Managers bypass phase restrictions (server uses forceAllowAllActions)
  const isCreateDenied =
    !isManagerView &&
    currentPeriod?.currentPhase?.createGoals === ObjectivePhaseActionType.Deny;

  const [filter, setFilter] = useGoalFilter(
    isManagerView,
    !isManagerView ? 'myGoals' : 'myReportsGoals',
  );

  const { selectedGoalId, setSelectedGoalId } = useSelectedGoal();
  const [isPullGoalModalVisible, setIsPullGoalModalVisible] = useState(false);

  const handleFilterChange = useCallback(
    (filter: GoalFilter) => {
      setFilter(filter);
    },
    [setFilter],
  );

  const handleCloseSidebar = useCallback(
    () => setSelectedGoalId(null),
    [setSelectedGoalId],
  );

  const handleCreateGoal = useCallback(
    () => setSelectedGoalId(CREATE_GOAL_ID),
    [setSelectedGoalId],
  );

  const handleOpenPullGoalModal = useCallback(() => {
    setIsPullGoalModalVisible(true);
  }, []);

  const handleClosePullGoalModal = useCallback(() => {
    setIsPullGoalModalVisible(false);
  }, []);

  const handleGoalClick = useCallback(
    (goalId: GoalId) => {
      setSelectedGoalId(goalId);
    },
    [setSelectedGoalId],
  );

  const handleFilterTypeChange = useCallback(
    (type: string | null) => {
      if (!isGoalFilterType(type)) {
        return;
      }
      setFilter({ ...filter, filterType: type });
    },
    [filter, setFilter],
  );

  const filterTypes = getFilterTypes(isManagerView);

  return (
    <GoalSelectionProvider>
      <Row>
        <Col className="d-flex align-items-center justify-content-between ">
          <GoalPeriodSelector />
          {(currentPeriod || !developmentFrameworkEnabled) && (
            <div className="d-flex gap-2 mt-3">
              {developmentFrameworkEnabled && (
                <Button
                  variant="outline-secondary"
                  onClick={handleOpenPullGoalModal}
                  label={t(`${i18prefix}.pullGoal.button`)}
                />
              )}
              <Button
                variant="primary"
                onClick={handleCreateGoal}
                label={t(`${i18prefix}.createGoal`)}
                disabled={isCreateDenied}
              />
            </div>
          )}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="d-flex justify-content-between align-items-center">
          <Tabs
            activeKey={filter.filterType}
            onSelect={handleFilterTypeChange}
            className="border-bottom-0">
            {filterTypes.map((filterType) => (
              <Tab
                key={filterType}
                eventKey={filterType}
                title={t(`${i18prefix}.filterType.${filterType}`)}
              />
            ))}
          </Tabs>
          {isManagerView && hasAchievementScales && (
            <Link to={managerRoutes.goalsOverview.route.create({})}>
              {t(`${i18prefix}.scoringOverview`)}
            </Link>
          )}
        </Col>
      </Row>
      <GoalsListWithFilter
        onCreateGoal={handleCreateGoal}
        onGoalClick={handleGoalClick}
        filter={filter}
        onFilterChange={handleFilterChange}
      />
      {isManagerView && <BulkApprovalBar />}
      <GoalSidebar
        goalId={selectedGoalId !== CREATE_GOAL_ID ? selectedGoalId : null}
        visible={selectedGoalId !== null}
        onClose={handleCloseSidebar}
      />
      <PullGoalModal
        visible={isPullGoalModalVisible}
        onClose={handleClosePullGoalModal}
      />
    </GoalSelectionProvider>
  );
};

const getFilterTypes = (isManager: boolean): GoalsFilterType[] => {
  if (!isManager) {
    return ['myGoals', 'colleaguesGoals', 'allGoals'];
  } else {
    return ['myReportsGoals', 'allGoals'];
  }
};
